import { media } from "../../design/breakpoints";
import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef
} from "react";
import axios from "axios";
import styled from "styled-components";
import { useHistory } from "react-router-dom";

const TableWrapper = styled.div`
  background: white;
  padding: 1rem;
  margin: 1rem auto;
  width: 60%;
  border-radius: 8px;
  box-shadow: 0 0 8px rgba(0,0,0,0.1);
`;

const MotorHeading = styled.h3`
  margin-bottom: 2px;
  font-size: 1.2rem;
  color: #be6f72;
  font-weight: bold;

  ${media.max1024} {
    font-size: 1.1rem;
  }

  ${media.max768} {
    font-size: 1rem;
  }

  ${media.max480} {
    font-size: 0.9rem;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  font-size: 1rem;

  th, td {
    border: 1px solid #ddd;
    padding: 0.6rem;
    text-align: center;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #fafafa;
  }

  ${media.max1024} {
    font-size: 0.95rem;
  }

  ${media.max768} {
    font-size: 0.9rem;
    th, td {
      padding: 0.4rem;
    }
  }

  ${media.max480} {
    font-size: 0.8rem;
    th, td {
      padding: 0.3rem;
    }
  }
`;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const MotorRuntimeTable = forwardRef(({ motors, startDate, endDate, token }, ref) => {
  const exportRef = useRef();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const [selectedMotor, setSelectedMotor] = useState(motors?.[0] || null);

  useImperativeHandle(ref, () => ({
    getElement: () => exportRef.current,
  }));

  const mode = (() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (startDate === endDate) return "daily";
    if (diffDays === 7) return "weekly";
    const startIsFirst = start.getDate() === 1;
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    if (startIsFirst && end.getDate() === nextMonth.getDate()) return "monthly";
    return "daily";
  })();

  const month = new Date(startDate).getMonth() + 1;
  const year = new Date(startDate).getFullYear();

  useEffect(() => {
    async function fetchAll() {
      const results = [];
      for (const motor of motors) {
        let url = "";
        if (mode === "daily") {
          url = `${process.env.REACT_APP_API}/sensor/motor-runtime?motor_id=${motor.motor_id}&start_date=${startDate}&end_date=${endDate}`;
        } else if (mode === "weekly") {
          url = `${process.env.REACT_APP_API}/sensor/motor-weekly-runtime?motor_id=${motor.motor_id}&start_date=${startDate}`;
        } else if (mode === "monthly") {
          url = `${process.env.REACT_APP_API}/sensor/motor-monthly-runtime?motor_id=${motor.motor_id}&month=${month}&year=${year}`;
        }

        const res = await axios.get(url, {
          headers: { Authorization: token[0] },
        });

        results.push({
          motor_id: motor.motor_id,
          location: res.data.location,
          logs: res.data.logs || res.data.days || [],
          total:
            res.data.total_run_time_hours ||
            res.data.total_weekly_run_time ||
            res.data.total_monthly_run_time,
        });
      }
      setData(results);
      setLoading(false);
    }
    fetchAll();
  }, [motors, startDate, endDate, token, mode, month, year]);

  useEffect(() => {
    if (motors && motors.length > 0) {
      setSelectedMotor(motors[0]);
    }
  }, [motors]);

  if (loading) return <TableWrapper>Loading motor runtime...</TableWrapper>;

  const motorLabel = selectedMotor
    ? selectedMotor.sensor_type?.toLowerCase().includes("pump")
      ? "Pump"
      : "Borewell"
    : "Motor";

  if (mode === "daily") {
    return (
      <TableWrapper ref={exportRef}>
        <MotorHeading>Run Time Report Of&nbsp;                  
                      {startDate === endDate
                    ? formatDate(startDate)
                    : `${formatDate(startDate)} to ${formatDate(endDate)}`}</MotorHeading>
        <StyledTable>
          <thead>
            <tr>
              {/* <th>Date</th> */}
              <th>{motorLabel}</th>
              <th>Runtime</th>
            </tr>
          </thead>
          <tbody>
            {data.map((motor, idx) => (
              <tr key={idx}>
                {/* <td>
                  {startDate === endDate
                    ? formatDate(startDate)
                    : `${formatDate(startDate)} to ${formatDate(endDate)}`}
                </td> */}
                <td>{motor.location}</td>
                <td>{motor.total}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    );
  }

  if (mode === "weekly") {
    return (
      <TableWrapper ref={exportRef}>
        <MotorHeading>Weekly Runtime Report from {formatDate(startDate)} to {formatDate(endDate)} </MotorHeading>
        <StyledTable>
          <thead>
            <tr>
              {/* <th>Period</th> */}
              <th>{motorLabel}</th>
              <th>Total Weekly Runtime</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {data.map((motor, idx) => (
              <tr key={idx}>
                {/* <td>{formatDate(startDate)} to {formatDate(endDate)}</td> */}
                <td>{motor.location}</td>
                <td>{motor.total}</td>
                <td>
                  <p
                    onClick={() =>
                      // history.push(`/weekly-detail?motor_id=${motor.motor_id}&start=${startDate}&end=${endDate}`)
                      history.push({
                  pathname: "/weekly-detail",
                  search: `?motor_id=${motor.motor_id}&start=${startDate}&end=${endDate}`,
                  state: { fromPage: "weeklyReport", filters: { startDate, endDate } }
                })

                    }
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    View
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    );
  }

  if (mode === "monthly") {
    const monthName = new Date(year, month - 1).toLocaleString("default", { month: "long" });
    return (
      <TableWrapper ref={exportRef}>
        <MotorHeading>Monthly Runtime Report Of {monthName}</MotorHeading>
        <StyledTable>
          <thead>
            <tr>
              {/* <th>Month</th> */}
              <th>{motorLabel}</th>
              <th>Total Runtime</th>
            </tr>
          </thead>
          <tbody>
            {data.map((motor, idx) => (
              <tr key={idx}>
                {/* <td>{monthName}</td> */}
                <td>{motor.location}</td>
                <td>{motor.total}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    );
  }

  return null;
});

export default MotorRuntimeTable;



